/* eslint-disable jsx-a11y/no-autofocus */

import InputModal from '@pcln/input-modal'
import React, { memo, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { SpaceProps, WidthProps } from 'styled-system'
import { themeGet } from '@styled-system/theme-get'
import { Box, Button, Flex, Grid, Text, Tooltip, getPaletteColor } from 'pcln-design-system'
import { User as UserIcon, ArrowLeft as LeftIcon } from 'pcln-icons'
import Popover from 'pcln-popover'
import TravelerSelectionContent from '../TravelerSelectionContent'
import useTravelerSelection from '../useTravelerSelection'
import { DEFAULT_MAX_TRAVELERS } from '../constants'
import { ITravelerSelection } from '../types'

const Container = styled(Flex)`
  position: relative;
  height: ${(props) => props.height}px;

  & > :last-child {
    width: 100%;
    ${(props) => (props.isOpen ? 'z-index: 1502' : '')};
  }
`

const StyledPopover = styled(Popover)`
  position: relative;
  max-width: 388px;
  padding: ${themeGet('space.1')} !important;
`

const TravelerSelectionButton = styled(Button)`
  &&& {
    display: flex;
    align-items: center;
    height: 100%;
    font-size: ${themeGet('fontSizes.2')}px;
    font-weight: normal;
    border-width: 1px;
    border-style: solid;
    border-color: ${(props) =>
      props.hasError ? getPaletteColor('error.base')(props) : getPaletteColor('border.base')(props)};
    outline: none;

    &:focus {
      border: 1px solid ${getPaletteColor('primary.base')};
      box-shadow: 0 0 0 2px ${getPaletteColor('primary.base')};
    }

    & > * {
      width: 100%;
    }

    ${themeGet('mediaQueries.lg')} {
      font-size: ${themeGet('fontSizes.1')}px;
    }
  }
`

const StyledTooltip = styled(Tooltip)`
  top: 20px;
  z-index: 9;

  &::after {
    &:focus {
      outline: none;
    }
  }
`

const StyledBackButton = styled(Button)`
  z-index: 1502;
`

/** @public */
const TravelerSelection = ({
  ageSelectEnabled,
  borderRadius = 'lg',
  className,
  disclaimer,
  height = 42,
  initialValues,
  isChildAgeSeti,
  isMobile = false,
  isInternationalFlight,
  isTravelerSelectionDisabled,
  isWhiteLabel,
  maxAdults,
  maxChildren,
  maxRooms,
  maxRoomsMessage,
  maxRoomsUrl,
  maxTravelers = DEFAULT_MAX_TRAVELERS,
  roomsEnabled,
  fireChildCountImpression = () => {},
  onChange,
  trapFocus = true,
  subLabelMap,
  lapInfantLabel,
  shouldCountLapInfant,
  showBackButton = false,
  usePopoverPortal = false,
  ...props
}: ITravelerSelection & SpaceProps & WidthProps) => {
  const useTravelerSelectionProps = useMemo(
    () => ({
      initialValues: {
        rooms: roomsEnabled ? 1 : 0,
        ...initialValues,
      },
      ageSelectEnabled: !!ageSelectEnabled,
      isInternationalFlight: !!isInternationalFlight,
      isWhiteLabel,
      maxTravelers,
      shouldCountLapInfant,
    }),
    [initialValues, roomsEnabled]
  )

  const [isOpen, setOpen] = useState(false)
  const state = useTravelerSelection(useTravelerSelectionProps)

  const modalIdx = 'traveler-selection-modal'
  const popoverIdx = 'traveler-selection-popover'
  const selectorAriaLabel = 'Select traveler information'

  useEffect(() => {
    if (!isOpen) return

    if (!isMobile) {
      const selectTravelerBtn: HTMLElement = document.querySelector(
        `[aria-label="${selectorAriaLabel}"]`
      ) as HTMLElement
      const popoverMenuBtn: HTMLElement = document.querySelector(
        `[aria-describedby="dialog-description-${popoverIdx}"]`
      ) as HTMLElement
      const popoverMenu: HTMLElement = document.querySelector(
        `[aria-describedby="dialog-description-${popoverIdx}"] > div`
      ) as HTMLElement

      popoverMenu.style.opacity = '0'

      setTimeout(() => {
        const { top: btnTop } = selectTravelerBtn.getBoundingClientRect()
        const { bottom: menuBottom, height: menuHeight } = popoverMenu.getBoundingClientRect()
        const isTopPlacement = popoverMenuBtn.attributes['data-placement'] === 'top'
        let currentTopValue
        let newTopValue

        if (menuBottom > window.innerHeight) {
          currentTopValue = parseInt(popoverMenu.style.top.replace(/^\D+/g, '')) || 0
          /* istanbul ignore next */
          newTopValue = isTopPlacement ? currentTopValue - menuHeight : Math.max(btnTop - menuHeight - 30, 0)
        } else {
          currentTopValue = parseInt(popoverMenuBtn.style.top.replace(/^\D+/g, '')) || 0
          /* istanbul ignore next */
          newTopValue = isTopPlacement ? currentTopValue - menuHeight : currentTopValue - 8
        }
        popoverMenuBtn.style.top = newTopValue + 'px'
        popoverMenu.style.opacity = '1'
      }, 10)
    }
  }, [isOpen])

  const popoverPortal = 'traveler-selection-popover-place'

  useEffect(() => {
    const rooms = roomsEnabled ? useTravelerSelectionProps.initialValues.rooms || 1 : 0

    state.setRooms(rooms)
    if (onChange) {
      onChange({ ...state, rooms })
    }
  }, [roomsEnabled, useTravelerSelectionProps.initialValues.rooms])

  if (state.children && isOpen && fireChildCountImpression) {
    fireChildCountImpression()
  }

  function onOpen() {
    setOpen(true)
  }

  function onClose() {
    setOpen(false)
    /* istanbul ignore else */
    if (onChange) {
      onChange(state)
    }
  }

  /* eslint-disable react/prop-types */
  function PopoverContent({ handleClose }) {
    return (
      <TravelerSelectionContent
        ageSelectEnabled={ageSelectEnabled}
        autoFocus={trapFocus}
        disclaimer={disclaimer}
        handleClose={handleClose}
        isChildAgeSeti={isChildAgeSeti}
        isPopover={!isMobile}
        lapInfantLabel={lapInfantLabel}
        maxAdults={maxAdults}
        maxChildren={maxChildren}
        maxRooms={maxRooms}
        maxRoomsMessage={maxRoomsMessage}
        maxRoomsUrl={maxRoomsUrl}
        roomsEnabled={roomsEnabled}
        shouldCountLapInfant={shouldCountLapInfant}
        state={state}
        subLabelMap={subLabelMap}
      />
    )
  }

  return (
    <>
      <Container className={className} isOpen={isOpen} width={1} height={height} {...props}>
        {!isMobile && (
          <StyledPopover
            idx={popoverIdx}
            hideArrow
            trapFocus={trapFocus}
            ariaLabel={selectorAriaLabel}
            width='388px'
            height={height}
            zIndex={1502}
            placement='bottom'
            renderContent={PopoverContent}
            onOpen={onOpen}
            onClose={onClose}
            querySelectorPortal={`.${popoverPortal}`}
          >
            <TravelerSelectionButton
              isOpen={isOpen}
              hasError={state.errorMessage && !isOpen}
              color='background.lightest'
              width={1}
              p={2}
              pl={isOpen && showBackButton && 0}
              disabled={isTravelerSelectionDisabled}
              borderRadius={borderRadius}
              onClick={onOpen}
            >
              {isOpen && showBackButton && (
                <StyledBackButton
                  color='primary'
                  onClick={onClose}
                  size='small'
                  data-testid='traveler-back-button'
                  mr={2}
                >
                  <LeftIcon color='white' />
                </StyledBackButton>
              )}
              <Flex alignItems='center' width={1}>
                <UserIcon color='primary' mr={2} />
                {state.label}
              </Flex>
              {!isOpen && state.errorMessage && (
                <StyledTooltip width={250} color='error' right bottom>
                  {state.errorMessage}
                </StyledTooltip>
              )}
            </TravelerSelectionButton>
          </StyledPopover>
        )}
        {isMobile && (
          <>
            <TravelerSelectionButton
              color='background.lightest'
              width={1}
              p={2}
              disabled={isTravelerSelectionDisabled}
              borderRadius={borderRadius}
              onClick={onOpen}
            >
              <Flex alignItems='center' width={1}>
                <UserIcon color='primary' mr={2} />
                {state.label}
              </Flex>
              {!isOpen && state.errorMessage && (
                <StyledTooltip width={250} color='error' right bottom>
                  {state.errorMessage}
                </StyledTooltip>
              )}
            </TravelerSelectionButton>
            <InputModal
              id={modalIdx}
              footer={
                <Button borderRadius='lg' onClick={onClose}>
                  Done
                </Button>
              }
              input={
                <Grid alignItems='center' templateColumns='1fr auto' pb={2}>
                  <Flex justifyContent='center'>
                    <Text fontSize='3' fontWeight='bold' color='primary.dark'>
                      Travelers
                    </Text>
                  </Flex>
                  <Box width='40px' height='40px' ml='12px' />
                </Grid>
              }
              inputProps={{ px: 2, py: 2 }}
              isOpen={isOpen}
              subheading={
                <Flex justifyContent='center' width={1}>
                  <Text fontSize='2' fontWeight='medium' color='text' mb={2}>
                    {state.label}
                  </Text>
                </Flex>
              }
              zIndex={1502}
              onClose={onClose}
            >
              <TravelerSelectionContent
                ageSelectEnabled={ageSelectEnabled}
                autoFocus={trapFocus}
                disclaimer={disclaimer}
                handleClose={onClose}
                isChildAgeSeti={isChildAgeSeti}
                lapInfantLabel={lapInfantLabel}
                maxAdults={maxAdults}
                maxChildren={maxChildren}
                maxRooms={maxRooms}
                maxRoomsMessage={maxRoomsMessage}
                maxRoomsUrl={maxRoomsUrl}
                roomsEnabled={roomsEnabled}
                shouldCountLapInfant={shouldCountLapInfant}
                state={state}
                subLabelMap={subLabelMap}
              />
            </InputModal>
          </>
        )}
      </Container>
      {usePopoverPortal ? <Box className={popoverPortal} width={0} data-testid='popover-portal' /> : null}
    </>
  )
}

export default memo(TravelerSelection)
